/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Tooltip} from "@mui/material";
import {usePostRequest} from "../../../../../../api/helper";
import ADAS_PROGRAMS_CONSTANTS from "../../../constants";
import React, {useEffect, useMemo, useState} from "react";
import toast from "react-hot-toast";
import {MRT_Cell, MRT_ColumnDef, MRT_RowSelectionState} from "material-react-table";
import cloneDeep from "lodash/cloneDeep";
import ArgosFlexbox from "../../../../Containers/ArgosFlexbox";
import Loader from "../../../../Loader/Loader";
import ArgosTextField from "../../../../Form/TextField/ArgosTextField";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";

import {ArgosTableSimple} from "../../../../Table/ArgosTableSimple";

/**
 * Determines whether the report format is disabled based on the provided conditions.
 *
 * @param {string} rowReportFormat - The format of the report for the current row.
 * @param {string[]} _rcsReportFormats - List of RCS-specific report formats.
 * @param {string[]} _sizeReportFormats - List of report formats requiring object size.
 * @param {boolean} _anyObjectsWithDefinedSize - Indicates if at least one object has a defined size (width and height).
 * @param {boolean} _anyObjectsWithDefinedSizeAndRCS - Indicates if at least one object has a defined size and RCS value.
 * @returns {boolean} A boolean value indicating whether the report format is disabled.
 */
export const isRowDisabled = (
  rowReportFormat: string,
  _rcsReportFormats: string[],
  _sizeReportFormats: string[],
  _anyObjectsWithDefinedSize: boolean,
  _anyObjectsWithDefinedSizeAndRCS: boolean
): boolean => {
  if (isDisabledDueToRCS(rowReportFormat, _rcsReportFormats, _anyObjectsWithDefinedSizeAndRCS)) {
    return true;
  } else if (isDisabledDueToSize(rowReportFormat, _sizeReportFormats, _anyObjectsWithDefinedSize)) {
    return true;
  } else {
    return false;
  }
};

/**
 * Checks if the row is disabled due to missing objects with defined size and RCS.
 *
 * @param {string} rowReportFormat - The format of the report for the current row.
 * @param {string[]} _rcsReportFormats - List of RCS-specific report formats.
 * @param {boolean} _anyObjectsWithDefinedSizeAndRCS - Indicates if at least one object has a defined size and RCS value.
 * @returns {boolean} True if the row is disabled due to this condition.
 */
export const isDisabledDueToRCS = (
  rowReportFormat: string,
  _rcsReportFormats: string[],
  _anyObjectsWithDefinedSizeAndRCS: boolean
): boolean => {
  return !_anyObjectsWithDefinedSizeAndRCS && _rcsReportFormats.includes(rowReportFormat);
};

/**
 * Checks if the row is disabled due to missing objects with defined size.
 *
 * @param {string} rowReportFormat - The format of the report for the current row.
 * @param {string[]} _sizeReportFormats - List of report formats requiring object size.
 * @param {boolean} _anyObjectsWithDefinedSize - Indicates if at least one object has a defined size (width and height).
 * @returns {boolean} True if the row is disabled due to this condition.
 */
export const isDisabledDueToSize = (
  rowReportFormat: string,
  _sizeReportFormats: string[],
  _anyObjectsWithDefinedSize: boolean
): boolean => {
  return !_anyObjectsWithDefinedSize && _sizeReportFormats.includes(rowReportFormat);
};

export const RCS_FORMAT_DISABLED_MESSAGE = 'This report format is disabled. You must select at least one object with a width, height, and rcs value.'
export const SIZE_FORMAT_DISABLED_MESSAGE = 'This report format is disabled. You must select at least one object with a width and height.'

/**
 * Returns tooltip props based on the report format and object conditions.
 *
 * @param {string} rowReportFormat - The format of the report for the current row.
 * @param {string[]} rcsReportFormats - List of RCS-specific report formats.
 * @param {string[]} sizeReportFormats - List of size-specific report formats.
 * @param {boolean} anyObjectsWithDefinedSize - Indicates if at least one object has a defined size (width and height).
 * @param {boolean} anyObjectsWithDefinedSizeAndRCS - Indicates if at least one object has a defined size and RCS value.
 * @returns {object} Tooltip props or an empty object.
 */
export const getTooltipProps = (
  rowReportFormat: string,
  rcsReportFormats: string[],
  sizeReportFormats: string[],
  anyObjectsWithDefinedSize: boolean,
  anyObjectsWithDefinedSizeAndRCS: boolean
): object => {
  if (isDisabledDueToRCS(rowReportFormat, rcsReportFormats, anyObjectsWithDefinedSizeAndRCS)) {
    return {
      component: Tooltip,
      title: RCS_FORMAT_DISABLED_MESSAGE,
    };
  } else if (isDisabledDueToSize(rowReportFormat, sizeReportFormats, anyObjectsWithDefinedSize)) {
    return {
      component: Tooltip,
      title: SIZE_FORMAT_DISABLED_MESSAGE,
    };
  }
  return {};
};

export const SelectName = (props: any) => {
  const {selectedDataset} = props
  const {page, multiSelect, reportName, setReportName, reportFormat, setReportFormat, selectedObjects, selectedReports, setSelectedReports} = props
  const datasetId = selectedDataset?.id

  /**
   * Fetch report formats for selected line and objects
   */
  const fetchReportFormatsHandle = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.coverageRequirements.fetchReportFormatsEndpoint)
  const [isLoadingGetReportFormats, setIsLoadingGetReportFormats] = useState(false)
  const [reportFormats, setReportFormats] = useState<string[]>([])
  const [selectFormatAndNameTableData, setSelectFormatAndNameTableData] = useState<Row[]>([])
  const [anyObjectsWithDefinedSize, setAnyObjectsWithDefinedSize] = useState(false) // If at least one of the selected objects has a defined size, this is true. Otherwise, disable all formats.
  const [anyObjectsWithDefinedSizeAndRCS, setAnyObjectsWithDefinedSizeAndRCS] = useState(false) // If at least one of the selected objects has a defined size and RCS, this is true. Otherwise, disable all rcs formats.
  const [rcsReportFormats, setRcsReportFormats] = useState<string[]>([])
  const [sizeReportFormats, setSizeReportFormats] = useState<string[]>([])
  const fetchReportFormats = () => {
    if (datasetId == null || !(datasetId?.length > 0) || selectedObjects == null || Object.keys(selectedObjects).length === 0) return null
    setIsLoadingGetReportFormats(true)
    const body = {page, datasetId, selectedObjects}
    fetchReportFormatsHandle(body).then((res) => {
      setReportFormats(res.reportFormats)
      setAnyObjectsWithDefinedSize(res.anyObjectsWithDefinedSize)
      setAnyObjectsWithDefinedSizeAndRCS(res.anyObjectsWithDefinedSizeAndRCS)
      setRcsReportFormats(res.rcsReportFormats)
      setSizeReportFormats(res.sizeReportFormats)
      // Don't overwrite the name if it is already set.
      setSelectFormatAndNameTableData(prevData => {
        // Create a map for quick lookup of existing names
        const nameMap = new Map(prevData.map(row => [row.format, row.name]))
        return res.reportFormats.map((format: string) => ({
          format,
          name: nameMap.get(format) && nameMap.get(format) !== '' ? nameMap.get(format)! : ''
        }))
      })
    }).catch((err) => {
      console.log(err)
      toast.error('Error fetching report formats')
    }).finally(() => {
      setIsLoadingGetReportFormats(false)
    })
  }

  useEffect(() => {
    fetchReportFormats()
  }, [datasetId, selectedObjects])

  const handleReportFormatChange = (event: any) => {
    setReportFormat(event.target.value)
  }

  interface Row {
    format: string
    name: string
  }

  const handleSaveCell = (cell: MRT_Cell<Row>, value: any) => {
    const _rowsData = cloneDeep(selectFormatAndNameTableData)
    _rowsData[cell.row.index][cell.column.id as keyof Row] = value
    setSelectFormatAndNameTableData(_rowsData)
  }

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})

  /**
   * Reset the report names of rows that are not selected
   */
  useEffect(() => {
    const _rowData = cloneDeep(selectFormatAndNameTableData)
    selectFormatAndNameTableData.forEach((data: any, index: number) => {
      if (!Object.keys(rowSelection).includes(index.toString())) {
        _rowData[index].name = ''
      }
    })
    setSelectFormatAndNameTableData(_rowData)
  }, [rowSelection])

  /**
   * Update selectedReports when selectFormatAndNameTableData changes
   */
  useEffect(() => {
    const _selectedReports: any = {}
    selectFormatAndNameTableData.forEach((row: any, index: number) => {
      if (Object.keys(rowSelection).includes(index.toString())) {
        _selectedReports[row.format] = row.name
        if (isRowDisabled(row.format, rcsReportFormats, sizeReportFormats, anyObjectsWithDefinedSize, anyObjectsWithDefinedSizeAndRCS)) {
          // remove checkbox index from rowSelection if disabled report format
          delete rowSelection[index]
        }
      }
    })
    setSelectedReports(_selectedReports)
  }, [selectFormatAndNameTableData, rcsReportFormats, sizeReportFormats, anyObjectsWithDefinedSize, anyObjectsWithDefinedSizeAndRCS])

  const selectFormatAndNameTableColumns = useMemo<MRT_ColumnDef<Row>[]>(
    () => [
      {
        accessorKey: "format",
        header: "Report Format",
        enableEditing: false,
      },
      {
        accessorKey: "name",
        header: "Report Name",
        grow: true,
        enableEditing: (row) => row.index in rowSelection,
        muiEditTextFieldProps: ({cell}) => ({
          onKeyDown: (e) => {
            if (e.code == "Tab") {
              e.stopPropagation()
              e.preventDefault()
            }
          },
          onBlur: (event) => {
            handleSaveCell(cell, event.target.value)
          }
        })
      }
    ], [selectFormatAndNameTableData, rowSelection]
  )

  return (
    <ArgosFlexbox vertical style={{height: '100%', width: '100%'}}>
      <Loader isLoading={isLoadingGetReportFormats}>
        {!multiSelect && <ArgosFlexbox vertical style={{flex: 1, padding: "20px"}} alignItems="center">
          <ArgosFlexbox vertical style={{flex: 1, width: "100%"}}>
            <ArgosTextField
              required
              key={'reportName'}
              onChange={(event) => {
                setReportName(event.target.value)
              }}
              value={reportName}
              label={'Report Name'}
              style={{paddingBottom: "20px"}}
            />
            <FormControl>
              <FormLabel id="report-format-form-group-label">Report Format</FormLabel>
              <RadioGroup
                aria-labelledby="report-format-form-group-label"
                name="report-format-form-group"
                value={reportFormat}
                onChange={handleReportFormatChange}
              >
                {reportFormats.map((reportFormat: string) => (
                  <FormControlLabel value={reportFormat} control={<Radio/>} label={reportFormat}/>
                ))}
              </RadioGroup>
            </FormControl>
          </ArgosFlexbox>
        </ArgosFlexbox>}
        {multiSelect && <ArgosFlexbox vertical style={{flex: 1, padding: "20px"}} alignItems="center">
          <ArgosFlexbox vertical style={{flex: 1, width: "100%"}}>
            <ArgosTableSimple
              columns={selectFormatAndNameTableColumns}
              data={selectFormatAndNameTableData}
              enableEditing
              editDisplayMode="cell"
              // Do not allow editing of disabled report formats
              enableRowSelection={(row) => {
                return !isRowDisabled(row.getValue('format'), rcsReportFormats, sizeReportFormats, anyObjectsWithDefinedSize, anyObjectsWithDefinedSizeAndRCS)
              }}
              enableStickyHeader={true}
              onRowSelectionChange={setRowSelection}
              state={{rowSelection}}
              // Show tooltip for disabled formats
              muiTableBodyRowProps={({row}) => {
                const rowReportFormat: string = row.getValue('format');
                return getTooltipProps(rowReportFormat, rcsReportFormats, sizeReportFormats, anyObjectsWithDefinedSize, anyObjectsWithDefinedSizeAndRCS);
              }}
            />
          </ArgosFlexbox>
        </ArgosFlexbox>}
      </Loader>
    </ArgosFlexbox>
  )
}